/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2020, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'./GroupOptimizerBase'
], (GroupOptimizerBase) => {

	function isObject(obj) {
		const type = typeof obj;
		return type === 'function' || type === 'object' && !!obj;
	}

	class SameCategoryDifferentMeasuresQueryCombiner  extends GroupOptimizerBase {
		constructor(queryService, dataSources) {
			super(queryService, dataSources);
			this.queryService = queryService;
			this.dataSources = dataSources;
			this.requests = [];
		}

		getId() {
			return 'SameCategoryDifferentMeasuresQueryCombiner';
		}

		/**
		 * @function SameCategoryDifferentMeasuresQueryCombiner#isApplicable
		 * @description checks if this optimizer is applicable for the passed request
		 * checks if query spec does not have all items as facts (aggregated)
		 * has measures from the same query subject and
		 * has atleast 1 measure and atleast 1 category
		 *
		 * @param {Object} requestParams request parameters
		 * @param {String} requestParams.sourceIdOrModule: The sourceId with which query is based on
		 * @param {Object} requestParams.querySpec  the v1 query spec
		 * @param {Object} requestParams.requestOptions additional request options
		 * @param {String} [requestParams.senderId] The unique string identifier of the the query sender
		 */
		isApplicable(requestParams){
			let { querySpec, sourceIdOrModule } = requestParams;

			if( !querySpec ) {
				return false;
			}

			if (querySpec.type) {
				//Non-default data queries such as minmax and detail are not supported.
				return false;
			}

			if(! querySpec.dataItems ) {
				return false;
			}

			//check if query spec does not have all items as facts (aggregated)
			const hasNoCategories = !querySpec.dataItems.find(dataItem => {
				return !dataItem.aggregate;
			});
			if( hasNoCategories) {
				return false;
			}

			//check request measures are from one query subject only
			let measuresTableId = null;
			let measuresHaveSameQuerySubject = true;
			const categoryIds = [];
			const measureIds = [];
			const dataSource = this.dataSources && this.dataSources.getDataSource(sourceIdOrModule);

			let containsOlap  = false;
			querySpec.dataItems.forEach( (dataItem) => {

				const metaDataColumn = dataSource && dataSource.getMetadataColumn(dataItem.itemId);

				if (!containsOlap && metaDataColumn && metaDataColumn.isOlapColumn()) {
					containsOlap = true;
				}

				if(dataItem.aggregate) {
					measureIds.push(dataItem.id);
					if(!measuresTableId) {
						measuresTableId = metaDataColumn && metaDataColumn.getTableId();
					} else {
						if( measuresTableId !== (metaDataColumn && metaDataColumn.getTableId()) ) {
							measuresHaveSameQuerySubject = false;
						}
					}

				} else {
					categoryIds.push(dataItem.id);
				}
			} );

			//VERSION1: There are some issues to be resolved for zero suppression in multi-category scenarios.
			//			In the meantime, turn off SameCategoryCombiner for olap.
			if(containsOlap || !measuresHaveSameQuerySubject ) {
				return false;
			}

			//check request has atleast 1 measure and 1 category
			const hasMeasure = querySpec.projections.some( (dataItemId) => {
				if(measureIds.includes(dataItemId)) {
					return true;
				}
			} );

			const hasCategory = querySpec.projections.some( (dataItemId) => {
				if(categoryIds.includes(dataItemId)) {
					return true;
				}
			} );

			return hasMeasure && hasCategory;
		}

		getRequestGroupKey(request) {
			//group requests that have the same
			// source id, filters, suppress, categories, measure query subject, suppress query hint & additional request options

			let requestParams = request.requestParams;
			const { queryKey, filterKey, measureQuerySubject } = this._getQueryKeys(requestParams);
			const queryHintsSuppress = requestParams.queryHints && requestParams.queryHints.suppress;
			const sourceId = requestParams.sourceId || requestParams.sourceIdOrModule;

			const requestGroupKey = sourceId
				+ '_' + (filterKey ? filterKey : 'noFilters')
				+ '_' + (requestParams.querySpec.suppress ? this._getCompareString(requestParams.querySpec.suppress, []) : 'noSuppress')
				+ '_' + (requestParams.querySpec.suppressZero ? this._getCompareString(requestParams.querySpec.suppressZero, []) : 'noSuppressZ')
				+ '_' + (queryKey ? queryKey : 'noCats')
				+ '_' + (measureQuerySubject ? measureQuerySubject : 'noMQS')
				+ '_' + (queryHintsSuppress ? queryHintsSuppress : 'noQHSuppress')
				+ '_'+ JSON.stringify(requestParams.requestOptions)+ '_' + JSON.stringify(requestParams.senderId);
			return requestGroupKey;
		}

		_getCategoryKey(dataItem, categories) {
			//VERSION1: Limit the optimizer to cases where ids and content for categories are identical.
			//Since keys for a given itemId are "predictable", this is only a minor limitation but
			//greatly simplifies the logic later on.  Different id support can be added later on.
			let key = dataItem.id;
			key += dataItem.itemId;	//The ColumnId
			if(dataItem.selection) {
				const itemSelection = this._getCompareString(dataItem.selection,['priority']);
				key += itemSelection;
			}
			if(dataItem.binning) {
				key += this._getCompareString(dataItem.binning,[]);
			}
			if(dataItem.nest) {
				//VERSION1: the content of the nest (dataItemIds) must be the same
				//			AND the dataItems they refer to need to be the same.
				key += this._getCompareString(dataItem.nest,[]);
				dataItem.nest.forEach((dataItemId) => {
					key += this._getCategoryKey(categories[dataItemId]);
				});
				//each element of the nesting needs to be part of the key.
			}
			return key;
		}

		_getQueryKeys(requestParams) {
			let key = '';
			let { querySpec, sourceIdOrModule } = requestParams;

			const dataSource = this.dataSources && this.dataSources.getDataSource(sourceIdOrModule);
			let measureQuerySubjectkey;

			const categories = {};
			const measureInfo = {};
			querySpec.dataItems.forEach( (dataItem) => {
				const metaDataColumn = dataSource && dataSource.getMetadataColumn(dataItem.itemId);

				if(!dataItem.aggregate) {
					categories[dataItem.id] = dataItem;
				} else {
					measureInfo[dataItem.id] = dataItem;
					if( !measureQuerySubjectkey ) {
						measureQuerySubjectkey = metaDataColumn && metaDataColumn.getTableId(); //all measures would be from same query subject
					}
				}
			} );

			//filters must be the same.  If there's a post-aggregate range filter on a dataitemId,
			//we must also ensure that the aggregation type of that item is the same.
			//(ie: don't want to consider Quantity Avg range 100-200 is the same as Quantity Max range 100-200)
			let filterKey = (requestParams.querySpec.filters && this._getCompareString(requestParams.querySpec.filters, []));
			querySpec.filters && querySpec.filters.forEach(( filter ) => {
				if (filter.expression && filter.expression.dataItem) {
					const relatedDataItem = measureInfo[filter.expression.dataItem];
					filterKey += (relatedDataItem) ? (filter.expression.dataItem + relatedDataItem.aggregate) : '';
				}
			});

			//Figure out what item (if any) drives the sort order for the query and make that part of the key.
			const sortOrderDrivenByCategories = this._sortOrderDrivenByCategories(querySpec, categories, measureInfo);

			querySpec.projections.forEach( (dataItemId) =>{
				if(categories[dataItemId]) {
					const dataItem = categories[dataItemId];
					key = key || '';
					key += this._getCategoryKey(dataItem, categories);
				} else if (measureInfo[dataItemId]) {
					//Must consider measures as part of the key if measures drive the sort OR have selections
					//(eg: Non-contextual TopN on one measure means we cant share a query with another measure)
					const selectionsExcludingSort = this._getMeasureCompareString(measureInfo[dataItemId].selection, sortOrderDrivenByCategories);
					if (selectionsExcludingSort.length) {
						//TODO: Ideally, we'd sort this for a possible match but we probably won't combine anyway.
						key += measureInfo[dataItemId].itemId;
						key += measureInfo[dataItemId].aggregate;
						key += selectionsExcludingSort;
					}
				}
			} );

			return  {
				queryKey : key,
				filterKey: filterKey,
				measureQuerySubject: measureQuerySubjectkey
			} ;
		}

		//return true if ALL the categories have a higher sort priority than any measure sort.
		_sortOrderDrivenByCategories(querySpec, categories, measureInfo) {
			const mappedDataItems = [];
			querySpec.projections.forEach((itemId) => {
				const dataItem = categories[itemId] || measureInfo[itemId];
				mappedDataItems.push(dataItem);
			});

			let lowestCategorySortPriority = -1, highestMeasureSortPriority = -1;

			mappedDataItems.forEach((dataItem) => {
				if (dataItem.selection) {
					dataItem.selection.forEach((selection) => {
						if (selection.operation==='order' && selection.sort && selection.sort.priority!==undefined) {
							if (dataItem.aggregate) {
								if (highestMeasureSortPriority === -1 || selection.sort.priority < highestMeasureSortPriority) {
									highestMeasureSortPriority = selection.sort.priority;
								}
							} else if (lowestCategorySortPriority === -1 || selection.sort.priority > lowestCategorySortPriority) {
								lowestCategorySortPriority = selection.sort.priority;
							}
						}
					});
				}
			});
			return (lowestCategorySortPriority!==-1 && lowestCategorySortPriority < highestMeasureSortPriority);
		}

		//Return the portion of the query key that represents the selection spec for a measure
		//which is the whole selection spec UNLESS a category drives the sort order.  If it does,
		//remove the measure sorts (they don't affect the result).
		_getMeasureCompareString(selection, isDrivingSortCategorical) {
			if (!selection) {
				return [];
			}

			let finalSelection = selection;
			if (isDrivingSortCategorical) {
				finalSelection = selection.filter((selectionEntry) => {
					return (!selectionEntry.sort);
				});
			}
			const compareString = this._getCompareString(finalSelection, []);
			return compareString;
		}

		_isOlap(dataSource, columnId) {
			//By default, suppression is ON for OLAP datasources, while for other datasources its OFF
			//we need to know if suppression is ON so that when we receive result back, we can appropriately remove rows for the widget suppression is ON
			return !!( columnId && dataSource && dataSource.getMetadataColumn && dataSource.getMetadataColumn(columnId).isOlapColumn());
		}

		executeGroup(requestInfoList) {

			const { optimizedQuery, infoInSentRequest } = this._prepareCombinedRequest(requestInfoList);

			this.queryService.runQuery({
				affinityContext: requestInfoList[0].request.requestParams.affinityContext,
				sourceIdOrModule: requestInfoList[0].request.requestParams.sourceIdOrModule,
				querySpec: optimizedQuery,
				requestOptions: requestInfoList[0].request.requestParams.requestOptions,
				sender: requestInfoList[0].request.requestParams.senderId
			}).then((response) => {

				this._processResponses(response, requestInfoList, infoInSentRequest);

			}).catch((jqXHR) => {
				for (let i = 0 ; i < requestInfoList.length; i++){
					const responseInfo = requestInfoList[i];
					responseInfo.response.reject( jqXHR );
				}
			});
		}

		//When there's a conflict in measureId's across queries, append the requestId as a suffix
		_getOptimizedQueryDataItemId(dataItemId, requestNumber) {
			return dataItemId + '_o' + requestNumber;
		}

		_prepareCombinedRequest(requestInfoList) {
			// start with the first querySpec
			const optimizedQuery = JSON.parse(JSON.stringify(requestInfoList[0].request.requestParams.querySpec));
			optimizedQuery.projections = [];
			optimizedQuery.dataItems = [];

			const infoInSentRequest = [];				//to lookup parts of the request


			const measures = {};						//to track measures across all requests, key is ItemId, value is DataItem

			//1. Prepare and fill optimizedQuery.dataItems
			this._prepareCombinedRequestDataItems(requestInfoList, infoInSentRequest, measures, optimizedQuery);

			//2. populate optimizedQuery.projections
			this._prepareCombinedRequestProjections(requestInfoList, infoInSentRequest, measures, optimizedQuery);

			return {
				optimizedQuery: optimizedQuery,
				infoInSentRequest: infoInSentRequest
			};
		}


		_prepareCombinedRequestDataItems( requestInfoList, infoInSentRequest, measures, optimizedQuery) {
			const processedDataItems = {};  	//to include dataItem once in optimizedQuery

			for (let i = 0 ; i < requestInfoList.length; i++){

				//Go over the queries and add the dataitems and project to the query spec;
				// prepare  infoInSentRequest
				// populate optimizedQuery.dataItems
				// populate measures
				infoInSentRequest[i] = {
					dataItems: {},	//shortcut for dataItems for request
					projections: [], //shortcut for projections for request
					measures:[],		//shortcut for measures for request
					suppressionOn: false, //to indicate whether to remove suppressed values from output
					dataItemsToApplySuppressionTo: undefined  //to be used later, just so we know this will be there
				};

				const sourceIdOrModule = requestInfoList[i].request.requestParams.sourceIdOrModule;
				const dataSource = this.dataSources && this.dataSources.getDataSource(sourceIdOrModule);

				//for dashboard, for v1 queries, it will be always be suppression off unless we are using OLAP or using table viz with suppression property off
				let isOlap = false;

				const querySpec = requestInfoList[i].request.requestParams.querySpec;

				for (let j = 0 ; j < querySpec.dataItems.length; j++){

					//track dataItems
					const dataItem = querySpec.dataItems[j];

					let optimizedQueryDataItemId = dataItem.id;

					//track measures
					if(dataItem.aggregate) {
						//For categories, we can guarantee the same content for the same id
						//because we use the entire category to generate the key.
						//For measures, we can't.  The same id in 2 widgets might have a different aggregation type for example.
						//When we get the same id, add a suffix (the request#) for the optimized query
						//TODO: Since we save selections now, the aggregationtype is likely the only difference 
						//      so we might be able to do this less often.  Better safe than sorry!
						if (measures[dataItem.id]) {
							optimizedQueryDataItemId = this._getOptimizedQueryDataItemId(dataItem.id, + i);
						}
						infoInSentRequest[i].measures.push(dataItem);
						measures[optimizedQueryDataItemId] = dataItem;
					}

					infoInSentRequest[i].dataItems[dataItem.id] = dataItem;
					if( !processedDataItems[optimizedQueryDataItemId] ) {
						if (optimizedQueryDataItemId !== dataItem.id) {
							const dataItemCopy = JSON.parse(JSON.stringify(dataItem));
							dataItemCopy.id = optimizedQueryDataItemId;
							optimizedQuery.dataItems.push(dataItemCopy);
						} else {
							optimizedQuery.dataItems.push(dataItem);
						}
						processedDataItems[optimizedQueryDataItemId] = 1;
					}

					if( !isOlap ){
						isOlap = this._isOlap(dataSource, dataItem.itemId);
					}
				}

				if( isOlap ) {
					infoInSentRequest[i].suppressionOn = true;
				}

				if(querySpec.suppress && querySpec.suppress.length === 1 && querySpec.suppress[0] === 'none') {
					infoInSentRequest[i].suppressionOn = false;
				}
			}
		}

		_prepareCombinedRequestProjections(requestInfoList, infoInSentRequest, measures, optimizedQuery) {

			const processedProjections = {};	//to include projections once in optimizedQuery

			for (let i = 0 ; i < requestInfoList.length; i++){
				const querySpec = requestInfoList[i].request.requestParams.querySpec;
				infoInSentRequest[i].optimizedQueryLocations = [];
				for (let projectionIndex = 0 ; projectionIndex < querySpec.projections.length; projectionIndex++){
					const dataItemId = querySpec.projections[projectionIndex];
					infoInSentRequest[i].projections.push(dataItemId);
					//As we process measure projections, always give preference ids with the request# suffix.
					//(ie: first try id_<request#>, then try id.  because id_<request#> might be different.
					const optimizedQueryDataItemId = measures[this._getOptimizedQueryDataItemId(dataItemId, i)] ? this._getOptimizedQueryDataItemId(dataItemId, i) : dataItemId;

					if( processedProjections[optimizedQueryDataItemId]===undefined) {
						optimizedQuery.projections.push(optimizedQueryDataItemId);
						processedProjections[optimizedQueryDataItemId] = optimizedQuery.projections.length-1;
					}

					//cross reference projection id's in the sent request to the location where that item is in the combined request.
					infoInSentRequest[i].optimizedQueryLocations.push({
						id: dataItemId,
						sourceLoc: infoInSentRequest[i].projections.length-1,
						optimizedQueryLoc: processedProjections[optimizedQueryDataItemId]
					});
				}
			}
		}

		_processResponses(response, requestInfoList, infoInSentRequest) {
			//split the response for each request and resolve

			let splitResponseData = [];

			//lookup data
			const responseDataItemIdList = [];
			const responseDataItemById = {};

			this._prepareLookupDataFromResponse(response, infoInSentRequest, responseDataItemIdList, responseDataItemById, splitResponseData);

			this._processResponseData(response, infoInSentRequest, responseDataItemIdList, splitResponseData);

			//reconstruct and resolve response for each request (into splitResponses)
			for (let i = 0 ; i < requestInfoList.length; i++){
				const responseInfo = requestInfoList[i];
				const data = {
					meta: response.data.meta,
					version: response.data.version,
					data: splitResponseData[i],
					dataItems: []
				};

				const projectedIdsInRequest = infoInSentRequest[i].projections;
				projectedIdsInRequest.forEach( (projectedDataItemId) => {
					const splitResponseDataItem = this._getDataItemForSplitResponse( responseDataItemById, projectedDataItemId, infoInSentRequest[i].dataItemsToApplySuppressionTo, i );
					data.dataItems.push( splitResponseDataItem );
				});

				responseInfo.response.resolve( {
					ETag: response.ETag,
					requestTime: response.requestTime,
					data: data,
					zipiId: response.zipiId
				} );
			}
		}

		_getDataItemForSplitResponse( responseDataItemById, projectedDataItemId, dataItemsToApplySuppressionTo, i ) {
			//Return the optimized query dataItem (in some cases, correcting it to match the original request).
			let responseDataItem = responseDataItemById[this._getOptimizedQueryDataItemId(projectedDataItemId, i)];
			if (responseDataItem) {
				//We need to remove any suffix we added when creating the optimized query.
				responseDataItem = JSON.parse(JSON.stringify(responseDataItem));
				responseDataItem.itemClass.id = projectedDataItemId;
			} else {
				//The response dataItem must always be copied to ensure post-processing happens on different instances (eg custom sort).
				responseDataItem = JSON.parse(JSON.stringify(responseDataItemById[projectedDataItemId] || {}));
			}

			//Zero suppression (OLAP only)
			//Manipulate the items array in the split response data (suppression case).
			if( dataItemsToApplySuppressionTo ) {
				const items = [];
				const deletedItems = dataItemsToApplySuppressionTo.byId[responseDataItem.itemClass.id];
				if( deletedItems ) {
					for( let i=0; i < responseDataItem.items.length; i++ ) {
						if( !deletedItems.includes(i) ) {
							items.push( responseDataItem.items[i]);
						}
					}
					responseDataItem.items = items;	//responseDataItem is a copy so we can modify it.
				}
			}
			return responseDataItem;
		}

		_prepareLookupDataFromResponse(response, infoInSentRequest, responseDataItemIdList, responseDataItemById, splitResponseData) {
			//prepare map for response data items
			response.data.dataItems.forEach( ( item ) => {
				responseDataItemById[item.itemClass.id] = item;
				responseDataItemIdList.push(item.itemClass.id);
			});

			//prepare empty splitResponseData
			for( let requestIndex=0; requestIndex < infoInSentRequest.length; requestIndex++ ) {
				splitResponseData[requestIndex] = [];
			}
		}

		_processResponseData(response, infoInSentRequest, responseDataItemIdList, splitResponseData) {
			//for all rows in response data, for each request, populate pt array based on projection for request

			let adjustSuppressedDataItems = false;
			for( let rowIndex=0; rowIndex < response.data.data.length; rowIndex++ ) {

				for( let requestIndex=0; requestIndex < infoInSentRequest.length; requestIndex++ ) {

					const suppressionOn = infoInSentRequest[requestIndex].suppressionOn;
					adjustSuppressedDataItems = suppressionOn;

					const sentRequestProjections = infoInSentRequest[requestIndex].projections;
					const optimizedQueryLocationInfo = infoInSentRequest[requestIndex].optimizedQueryLocations;
					let pt = [];
					let response_pt = response.data.data[rowIndex].pt;
					let suppressed = false;

					sentRequestProjections.forEach((projection, idx) => {
						const oqInfo = optimizedQueryLocationInfo[idx];
						const val = response_pt[oqInfo.optimizedQueryLoc];

						if( !isObject(val) ) {
							pt.push( val );
						} else {
							//is a measure value, put measure for request appropriately
							//if suppression was on for the request, remove rows with zero or null values
							if( suppressionOn && (( val.v === null ) ) ) {
								this._trackDataItemsToApplySuppressionTo( infoInSentRequest[requestIndex], response_pt, responseDataItemIdList );
								suppressed = true;
							} else {
								pt.push( val );
							}
						}
					});

					if(!suppressed) {
						splitResponseData[requestIndex].push({ pt: pt });
					}
				}
			}

			if(adjustSuppressedDataItems) {
				this._adjustSuppressedDataItems(splitResponseData, infoInSentRequest);
			}
		}

		_adjustSuppressedDataItems(splitResponseData, infoInSentRequest) {
			//go over again, this time process suppressions
			for( let requestIndex=0; requestIndex < splitResponseData.length; requestIndex++ ) {
				const dataForRequest = splitResponseData[requestIndex];

				for( let rowIndex= 0; rowIndex < dataForRequest.length; rowIndex++ ) {
					let dataRow = dataForRequest[rowIndex];
					let pt = dataRow.pt;
					for( let ptIndex=0; ptIndex< pt.length; ptIndex++) {
						if( !isObject( pt[ptIndex] )) {
							pt[ptIndex] = this._getCategoryValForRequest(infoInSentRequest[requestIndex], ptIndex, pt[ptIndex]);
						}
					}
				}
			}
		}

		_trackDataItemsToApplySuppressionTo( infoInSentRequest, response_pt, responseDataItemIdList ) {

			for( let ptIndex=0; ptIndex< response_pt.length; ptIndex++) {
				const val = response_pt[ptIndex];
				const id = responseDataItemIdList[ptIndex];

				if( !isObject(val) ) {
					infoInSentRequest.dataItemsToApplySuppressionTo = infoInSentRequest.dataItemsToApplySuppressionTo || {
						byIndex : [],
						byId : {}
					};
					infoInSentRequest.dataItemsToApplySuppressionTo.byIndex[ptIndex] = infoInSentRequest.dataItemsToApplySuppressionTo.byIndex[ptIndex] || {};
					infoInSentRequest.dataItemsToApplySuppressionTo.byIndex[ptIndex].removedItems = infoInSentRequest.dataItemsToApplySuppressionTo.byIndex[ptIndex].removedItems || [];
					infoInSentRequest.dataItemsToApplySuppressionTo.byIndex[ptIndex].removedItems.push(val);
					infoInSentRequest.dataItemsToApplySuppressionTo.byId[id] = infoInSentRequest.dataItemsToApplySuppressionTo.byId[id] || [];
					infoInSentRequest.dataItemsToApplySuppressionTo.byId[id].push(val);
				}
			}

		}

		_getCategoryValForRequest( infoInSentRequest, ptIndex, combinedResponseVal ) {
			let adjust = false;
			const byIndex = infoInSentRequest.dataItemsToApplySuppressionTo && infoInSentRequest.dataItemsToApplySuppressionTo.byIndex[ptIndex];
			if( byIndex ){
				const removedItems = byIndex.removedItems;
				const removedItemsCount = removedItems.length;
				for( let i=0; i<removedItemsCount; i++ ) {
					const val = removedItems[i];
					if(combinedResponseVal > val) {
						adjust = true;
						break;
					}
				}
				return adjust ? combinedResponseVal - removedItemsCount: combinedResponseVal;
			}
			return combinedResponseVal;
		}

	}

	return SameCategoryDifferentMeasuresQueryCombiner;
});
